import { buildDetailPath } from "@shared/navigation/tabs/detailRoutes";
import {
  resolveServiceStatusTag,
  serviceStatusRank,
  type ServiceStatusVariant,
} from "kontrolEdge/helpers/serviceStatusTag";

/** Kinds of logical connection an Extended PoP delivers (CTX-797). */
export type ConnectionType =
  | "internet"
  | "datacenter-interconnect"
  | "cloud-connectivity"
  | "fwint";

/** One service the Extended PoP delivers, as `/:id/connections` serves it. */
export interface ExtenderPopConnection {
  type: ConnectionType;
  id: number;
  /** Service ID (13-, 30-, 40-, 61-…). */
  serviceId: string;
  name: string | null;
  /** The customer port it rides on. */
  port: { id: number; serviceId: string };
  /** The CPE behind that port. */
  cpeId: number | null;
  status: string;
  statusMessage: string | null;
}

const TYPE_LABEL: Record<ConnectionType, string> = {
  internet: "Internet",
  "datacenter-interconnect": "Datacenter Interconnect",
  "cloud-connectivity": "Cloud Connectivity",
  fwint: "Firewall Interface",
};

export const connectionTypeLabel = (type: string): string =>
  TYPE_LABEL[type as ConnectionType] ?? type;

/**
 * Where a connection's ID leads: its own detail. A Firewall interface has
 * none — it goes to FWaaS, as it does from the Port connections table.
 */
export const connectionDetailPath = (
  connection: Pick<ExtenderPopConnection, "type" | "id">
): string | null => {
  switch (connection.type) {
    case "internet":
      return buildDetailPath("internet", { connectionId: connection.id });
    case "datacenter-interconnect":
      return buildDetailPath("datacenter", { datacenterId: connection.id });
    case "cloud-connectivity":
      return buildDetailPath("cloudConnectivity", {
        connectionId: connection.id,
      });
    case "fwint":
      return "/kontrol/fwaas";
    default:
      return null;
  }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const shapeConnections = (raw: any): ExtenderPopConnection[] =>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Array.isArray(raw?.connections) ? raw.connections : []).map((c: any) => ({
    type: c.type,
    id: c.id,
    serviceId: c.serviceId ?? "-",
    name: c.name ?? null,
    port: { id: c.port?.id, serviceId: c.port?.serviceId ?? "-" },
    cpeId: c.cpeId ?? null,
    status: c.status ?? "unknown",
    statusMessage: c.statusMessage ?? null,
  }));

const SUMMARY_ORDER: ConnectionType[] = [
  "internet",
  "datacenter-interconnect",
  "cloud-connectivity",
  "fwint",
];

/** One type of connection of the site, counted by status. */
export interface ConnectionSummaryRow {
  type: ConnectionType;
  label: string;
  total: number;
  /** Healthiest first: Active, Warning, Down, No data. */
  statuses: { label: string; variant: ServiceStatusVariant; count: number }[];
}

/**
 * The site's connections in a few rows — one per type, with how many there
 * are and how many are up — for the Extended PoP detail. Each CPE's detail
 * lists its own one by one.
 */
export const summarizeConnections = (
  connections: ExtenderPopConnection[]
): ConnectionSummaryRow[] =>
  SUMMARY_ORDER.flatMap((type) => {
    const ofType = connections.filter((connection) => connection.type === type);
    if (ofType.length === 0) return [];

    const byLabel = new Map<
      string,
      { label: string; variant: ServiceStatusVariant; count: number; rank: number }
    >();
    for (const connection of ofType) {
      const { label, variant } = resolveServiceStatusTag(connection.status);
      const entry = byLabel.get(label) ?? {
        label,
        variant,
        count: 0,
        rank: serviceStatusRank(connection.status),
      };
      entry.count += 1;
      byLabel.set(label, entry);
    }

    return [
      {
        type,
        label: connectionTypeLabel(type),
        total: ofType.length,
        statuses: Array.from(byLabel.values())
          .sort((a, b) => b.rank - a.rank)
          .map(({ label, variant, count }) => ({ label, variant, count })),
      },
    ];
  });
